import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Seo from "@/components/Seo";
import { FadeUp } from "@/components/Reveal";
import { Spark } from "@/components/Doodles";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputCls = "mt-1 w-full border-2 border-ink bg-cream px-4 py-3 text-ink";

const Activate = () => {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [msg, setMsg] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const requestActivation = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
            const { data } = await api.post("/auth/activate", { email, origin_url: window.location.origin });
            setMsg(data.message);
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail));
        }
        setBusy(false);
    };

    const doActivate = async (e) => {
        e.preventDefault();
        setError("");
        if (password !== confirm) {
            setError("Those two passwords don't match. Try again.");
            return;
        }
        setBusy(true);
        try {
            await api.post("/auth/activate/confirm", { token, name, password });
            setMsg("Account activated — welcome officially. Taking you to your card…");
            setTimeout(() => navigate("/account"), 1200);
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail));
            setBusy(false);
        }
    };

    return (
        <div className="flex min-h-screen items-center justify-center bg-forest px-5 py-24">
            <Seo title="Activate Membership" description="Activate your Panther City Clubhouse member account — already paying dues? Set your password here." />
            <FadeUp className="w-full max-w-md">
                <form onSubmit={token ? doActivate : requestActivation} data-testid="activate-form" className="border-2 border-ink bg-paper p-8 hard-shadow-lg rotate-1">
                    <Spark className="h-8 w-8 text-orange" />
                    <h1 className="mt-2 font-display text-3xl tracking-wide text-ink">{token ? "SET YOUR PASSWORD" : "ALREADY A PAYING MEMBER?"}</h1>
                    {msg ? (
                        <p data-testid="activate-success" className="mt-5 border-2 border-dashed border-forest bg-cream px-4 py-3 font-semibold text-forest">{msg}</p>
                    ) : token ? (
                        <>
                            <label className="mt-5 block text-sm font-semibold text-ink" htmlFor="activate-name">Name</label>
                            <input id="activate-name" data-testid="activate-name-input" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
                            <label className="mt-4 block text-sm font-semibold text-ink" htmlFor="activate-password">Password</label>
                            <input id="activate-password" data-testid="activate-password-input" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
                            <label className="mt-4 block text-sm font-semibold text-ink" htmlFor="activate-confirm">Once more</label>
                            <input id="activate-confirm" data-testid="activate-confirm-input" type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} />
                            <button type="submit" disabled={busy} data-testid="activate-submit-button" className="btn-stamp mt-6 w-full !bg-orange disabled:opacity-60">
                                {busy ? "Laminating…" : "Activate My Account"}
                            </button>
                        </>
                    ) : (
                        <>
                            <p className="mt-3 text-sm text-ink/70">
                                If you've already paid dues (in person, or before accounts existed), give us the email on your membership.
                                If it matches an active membership, we'll send an activation link — no paying twice.
                            </p>
                            <label className="mt-5 block text-sm font-semibold text-ink" htmlFor="activate-email">Email on your membership</label>
                            <input id="activate-email" data-testid="activate-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
                            <button type="submit" disabled={busy} data-testid="activate-request-button" className="btn-stamp mt-6 w-full !bg-orange disabled:opacity-60">
                                {busy ? "Checking the ledger…" : "Send Activation Link"}
                            </button>
                        </>
                    )}
                    {error && <p data-testid="activate-error" className="mt-3 font-semibold text-brick">{error}</p>}
                    <p className="mt-4 text-sm text-ink/60">
                        Not a member yet? <Link to="/membership" className="font-semibold text-forest underline decoration-mustard decoration-2 underline-offset-4">Join the club</Link>
                    </p>
                </form>
            </FadeUp>
        </div>
    );
};

export default Activate;
